/**
 * Shared logic for the patient record "My Visits" / "All Visits" / "Other
 * Visits" tabs.
 *
 * Visits are fetched once per patient; each tab is a pure filter over that
 * one list rather than a separate query, so "mine" vs "not mine" can never
 * drift between tabs.
 */

export type VisitTab = 'my-visits' | 'all-visits' | 'other-visits';

export const VISIT_TABS = {
  MINE: 'my-visits',
  ALL: 'all-visits',
  OTHER: 'other-visits',
} as const satisfies Record<string, VisitTab>;

export type VisitSource = 'composition' | 'diagnosticReport' | 'DocumentSource';

export interface VisitSummaryLike {
  id: string;
  encounterId?: string | null;
  issued?: string | null;
  codeText?: string;
  practitionerId?: string;
  practitionerName?: string;
}

export interface MergedVisitEntry<T extends VisitSummaryLike> {
  item: T;
  source: VisitSource;
}

/**
 * Merge DiagnosticReport, Composition, and DocumentSource summaries into one
 * row per visit. Priority order (highest to lowest): DocumentSource >
 * Composition > DiagnosticReport. Visits without an encounter reference fall
 * back to a composite key (practitioner + date + title) instead of a
 * per-resource unique key, so entries for the same visit still collapse into
 * one row even when neither carries an encounter reference.
 */
export function mergeVisitEntries<T extends VisitSummaryLike>(
  diagnosticReports: T[],
  compositions: T[],
  documentSources: T[] = []
): MergedVisitEntry<T>[] {
  const visitKey = (item: T) =>
    item.encounterId ||
    `no-enc:${item.practitionerId ?? ''}:${item.issued ?? ''}:${item.codeText ?? ''}`;

  const byVisit = new Map<string, MergedVisitEntry<T>>();

  for (const item of diagnosticReports) {
    byVisit.set(visitKey(item), { item, source: 'diagnosticReport' });
  }
  for (const item of compositions) {
    byVisit.set(visitKey(item), { item, source: 'composition' });
  }
  for (const item of documentSources) {
    const key = visitKey(item);
    const lower = byVisit.get(key)?.item;
    // A DocumentReference carries no performer, so replacing the lower-priority
    // entry outright would drop who owns the visit and push it out of "My Visits".
    const merged =
      lower && !item.practitionerId
        ? {
            ...item,
            practitionerId: lower.practitionerId,
            practitionerName: lower.practitionerName,
          }
        : item;
    byVisit.set(key, { item: merged, source: 'DocumentSource' });
  }

  return Array.from(byVisit.values());
}

function normalizePractitionerId(id: string | undefined | null): string {
  return (id || '').trim().toLowerCase();
}

export function isMyVisit(
  practitionerId: string | undefined,
  currentUserId: string | undefined
): boolean {
  if (!currentUserId || !practitionerId) return false;
  return normalizePractitionerId(practitionerId) === normalizePractitionerId(currentUserId);
}

/** Derive the visible list for one tab from the full, already-fetched visit list. */
export function filterVisitsByTab<T extends VisitSummaryLike>(
  entries: MergedVisitEntry<T>[],
  tab: VisitTab,
  currentUserId: string | undefined,
  selectedPractitionerId?: string
): MergedVisitEntry<T>[] {
  switch (tab) {
    case VISIT_TABS.MINE:
      return entries.filter((entry) => isMyVisit(entry.item.practitionerId, currentUserId));
    case VISIT_TABS.OTHER:
      return entries.filter((entry) => !isMyVisit(entry.item.practitionerId, currentUserId));
    case VISIT_TABS.ALL:
      if (!selectedPractitionerId) return entries;
      return entries.filter(
        (entry) =>
          normalizePractitionerId(entry.item.practitionerId) ===
          normalizePractitionerId(selectedPractitionerId)
      );
    default:
      return entries;
  }
}

/** Counts for tab labels — always derived from the one full list, so they stay in sync with the lists. */
export function countMyVsOther<T extends VisitSummaryLike>(
  entries: MergedVisitEntry<T>[],
  currentUserId: string | undefined
): { mine: number; other: number; all: number } {
  const mine = entries.filter((entry) =>
    isMyVisit(entry.item.practitionerId, currentUserId)
  ).length;
  return { mine, other: entries.length - mine, all: entries.length };
}
